import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { useOrderChanges, useRequestOrderChange } from '../../api/hooks';
import type { OrderChangeRequestRow, OrderDetail } from '../../api/types';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Money, Note, Panel } from '../../ui';
import { draftChanges, hasBadQuantity, totalWith, type Draft } from './order-edit';

type Line = OrderDetail['lines'][number];

/**
 * R-06 — "Edit Order": the retailer asks to change the quantity or size of lines, right in the order's own
 * table. Nothing about the order moves until Miraki approves; this only sends the request. Offered only
 * while the server says the order may still be changed, and it says why when it may not.
 *
 * The parts are handed to the page to place: the button beside the status, the notes and the send bar under
 * the header, and the two cells that turn into fields while editing.
 */
export function EditOrder(
  { order, currency, children }: {
    order: OrderDetail; currency: string | undefined;
    children: (parts: {
      button: ReactNode; notes: ReactNode; bar: ReactNode;
      quantityCell: (l: Line) => ReactNode; sizeCell: (l: Line) => ReactNode;
    }) => ReactNode;
  },
) {
  const { t } = useTranslation();
  const { me } = useSession();
  const changes = useOrderChanges('shop', order.id);
  const send = useRequestOrderChange(order.id);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const may = me?.kind === 'retailer' && me.permissions.includes('shop.order');
  const eligibility = changes.data?.eligibility;
  const fresh = (): Record<string, Draft> =>
    Object.fromEntries(order.lines.map((l) => [l.id, { quantity: String(l.quantity), size: l.size ?? '' }]));

  const made = draftChanges(order.lines, drafts);
  const badQty = hasBadQuantity(order.lines, drafts);
  const shownCurrency = currency ?? order.currency;

  function stop() { if (!send.isPending) { setEditing(false); setError(null); } }

  function submit() {
    setError(null);
    send.mutate(
      made.map((c) => ({
        lineId: c.line.id,
        ...(c.quantity !== c.line.quantity ? { quantity: c.quantity } : {}),
        ...(c.size !== (c.line.size ?? null) ? { size: c.size } : {}),
      })),
      {
        onSuccess: () => { setEditing(false); setSent(true); },
        onError: (e) => setError(e instanceof ApiError ? e.message : t('shop.orders.editFailed')),
      },
    );
  }

  const setDraft = (id: string, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { quantity: '', size: '' }), ...patch } }));

  const button = may && eligibility?.editable && !editing ? (
    <Button onClick={() => { setDrafts(fresh()); setSent(false); setError(null); setEditing(true); }}>{t('shop.orders.editOrder')}</Button>
  ) : null;

  const notes = (
    <>
      {may && eligibility && !eligibility.editable && eligibility.reason ? <Note>{eligibility.reason}</Note> : null}
      {sent ? <Alert tone="us">{t('shop.orders.editSent')}</Alert> : null}
    </>
  );

  const bar = editing ? (
    <Panel title={t('shop.orders.editTitle', { ref: order.reference })} pad>
      <p>{t('shop.orders.editHint')}</p>
      {made.length > 0 ? (
        <div>
          {t('shop.orders.editCurrentTotal')}: <Money amount={order.agreedTotal} currency={shownCurrency} />
          {' · '}
          <b>{t('shop.orders.editNewTotal')}: <Money amount={totalWith(order.lines, drafts)} currency={shownCurrency} /></b>
        </div>
      ) : <div className="mk-dim">{t('shop.orders.editNothing')}</div>}
      {error ? <div role="alert" className="mk-dim" style={{ marginTop: 'var(--mk-space-2)' }}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 'var(--mk-space-2)', marginTop: 'var(--mk-space-3)', flexWrap: 'wrap' }}>
        <Button tone="ghost" disabled={send.isPending} onClick={stop}>{t('common.cancel')}</Button>
        <Button disabled={send.isPending || made.length === 0 || badQty} onClick={submit}>
          {send.isPending ? t('shop.orders.editSending') : t('shop.orders.editSubmit')}
        </Button>
      </div>
    </Panel>
  ) : null;

  const quantityCell = (l: Line): ReactNode => (editing ? (
    <input
      className="mk-fld" inputMode="numeric" style={{ width: 80 }} value={drafts[l.id]?.quantity ?? ''}
      aria-label={`${t('shop.orders.editColQty')} ${l.variantCode}`}
      onChange={(e) => setDraft(l.id, { quantity: e.target.value })}
    />
  ) : l.quantity);

  const sizeCell = (l: Line): ReactNode => (editing ? (
    <input
      className="mk-fld" style={{ width: 90 }} maxLength={32} value={drafts[l.id]?.size ?? ''}
      aria-label={`${t('shop.orders.editColSize')} ${l.variantCode}`}
      onChange={(e) => setDraft(l.id, { size: e.target.value })}
    />
  ) : (l.size ?? '—'));

  return <>{children({ button, notes, bar, quantityCell, sizeCell })}</>;
}

const TONE = { PENDING: 'us', APPROVED: 'done', REJECTED: 'stop' } as const;

/** The retailer's own change requests on this order, newest first, with each one's status and any reason. */
export function ChangeRequestsPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const changes = useOrderChanges('shop', orderId);
  const rows = changes.data?.requests ?? [];
  if (rows.length === 0) return null;
  const label = (r: OrderChangeRequestRow) => t(`shop.orders.change${r.status === 'PENDING' ? 'Pending' : r.status === 'APPROVED' ? 'Approved' : 'Rejected'}`);
  return (
    <Panel title={t('shop.orders.changesTitle')} pad>
      {rows.map((r) => (
        <div key={r.id} style={{ marginBottom: 'var(--mk-space-4)' }}>
          <Chip tone={TONE[r.status]}>{label(r)}</Chip>{' '}
          <span className="mk-dim">{t('shop.orders.changeRequestedAt', { when: new Date(r.requestedAt).toLocaleString() })}</span>
          <ul>
            {r.lines.map((l) => (
              <li key={l.lineId}>
                {l.description ?? l.variantCode}:{' '}
                {[
                  l.quantityFrom !== l.quantityTo ? t('shop.orders.editQtyChange', { from: l.quantityFrom, to: l.quantityTo }) : null,
                  l.sizeFrom !== l.sizeTo
                    ? t('shop.orders.editSizeChange', { from: l.sizeFrom ?? t('shop.orders.editNoSize'), to: l.sizeTo ?? t('shop.orders.editNoSize') }) : null,
                ].filter(Boolean).join(', ')}
              </li>
            ))}
          </ul>
          {r.status === 'REJECTED' && r.decisionReason ? <div className="mk-dim">{t('shop.orders.changeRejectedWhy', { reason: r.decisionReason })}</div> : null}
          {r.decidedAt ? <div className="mk-dim">{t('shop.orders.changeDecidedAt', { when: new Date(r.decidedAt).toLocaleString() })}</div> : null}
        </div>
      ))}
    </Panel>
  );
}
